/**
 * Drag to fund (v8.0.0, Blueprint §13: "tap-to-act primary, drag as an
 * enhancement").
 *
 * Drag from the Unallocated tile onto an envelope and the SAME fund sheet a tap
 * opens comes up, for that envelope. Nothing moves on drop — the sheet is where
 * the amount is chosen and confirmed — so a drop that lands on the wrong tile
 * costs one "Not now", never money.
 *
 * Pointer events, not HTML5 drag-and-drop: one code path for mouse, pen and
 * touch. How well HTML5 drag-and-drop works with a finger in iPhone Safari was
 * not verified, and the iPhone is the target. The source tile carries
 * `touch-action: none` (canvas.css) so a finger on it drags rather than
 * scrolling; everywhere else scrolls as before. Mouse and pen need a few pixels
 * of travel first, so an ordinary click is never mistaken for a drag.
 *
 * Keyboard and screen-reader users lose nothing: every target tile is already a
 * button that opens the same sheet.
 *
 * DOM code. The arithmetic is in ./drag-geometry.ts, where the unit suite (which
 * runs in workerd, with no document) can reach it.
 */

import { ghostPosition, travelled } from './drag-geometry';

export interface DragFundOptions {
  /** The Unallocated tile. */
  source: HTMLElement;
  /** Whether an envelope id is a valid destination right now. */
  canDrop: (envelopeId: string) => boolean;
  onDrop: (envelopeId: string) => void;
  /** Read out when a drag starts and ends, for anyone watching a screen reader. */
  label?: string;
}

/** Wire one drag source. Returns a function that removes the listeners. */
export function enableDragToFund(opts: DragFundOptions): () => void {
  const { source } = opts;
  source.classList.add('tile-drag-source');

  let start: { x: number; y: number; id: number } | null = null;
  let dragging = false;
  let ghost: HTMLElement | null = null;
  let over: HTMLElement | null = null;

  const targetAt = (x: number, y: number): HTMLElement | null => {
    const hit = document.elementFromPoint(x, y);
    const tile = hit?.closest<HTMLElement>('[data-envelope-id]') ?? null;
    if (!tile || tile === source) return null;
    return opts.canDrop(tile.dataset.envelopeId!) ? tile : null;
  };

  const setOver = (tile: HTMLElement | null) => {
    if (tile === over) return;
    over?.classList.remove('is-drop-target');
    over = tile;
    over?.classList.add('is-drop-target');
  };

  const finish = () => {
    setOver(null);
    ghost?.remove();
    ghost = null;
    dragging = false;
    start = null;
    source.classList.remove('is-dragging');
    document.body.classList.remove('is-drag-funding');
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    start = { x: e.clientX, y: e.clientY, id: e.pointerId };
    // Touch has no hover and no click to protect: start at once. Mouse and pen
    // wait for travel, so a click on the tile stays a click.
    if (e.pointerType === 'touch') begin(e);
  };

  const begin = (e: PointerEvent) => {
    dragging = true;
    try {
      source.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic or already-released pointer cannot be captured; the
      // document-level listeners below still track it.
    }
    source.classList.add('is-dragging');
    document.body.classList.add('is-drag-funding');
    ghost = document.createElement('div');
    ghost.className = 'drag-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.textContent = opts.label ?? 'Drop on an envelope';
    document.body.append(ghost);
    move(e);
  };

  const move = (e: PointerEvent) => {
    if (ghost) {
      const { left, top } = ghostPosition(
        e.clientX,
        e.clientY,
        ghost.offsetWidth,
        window.innerWidth,
      );
      ghost.style.transform = `translate(${left}px, ${top}px)`;
    }
    setOver(targetAt(e.clientX, e.clientY));
  };

  const onMove = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return;
    if (!dragging) {
      if (!travelled(e.clientX - start.x, e.clientY - start.y)) return;
      begin(e);
    }
    e.preventDefault();
    move(e);
  };

  const onUp = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return;
    const wasDragging = dragging;
    const target = wasDragging ? targetAt(e.clientX, e.clientY) : null;
    finish();
    if (target) opts.onDrop(target.dataset.envelopeId!);
  };

  const onCancel = () => finish();

  source.addEventListener('pointerdown', onDown);
  document.addEventListener('pointermove', onMove, { passive: false });
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', onCancel);
  return () => {
    finish();
    source.classList.remove('tile-drag-source');
    source.removeEventListener('pointerdown', onDown);
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onCancel);
  };
}
